// React imports
import { useContext } from 'react';

// Third-party library imports
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';

// API imports
import { useGetCaseDetails } from '../../api/casedetails';

// Context imports
import UserProfileContext from '../../context/userprofilecontext.js';

// Utility imports
import { formatDate, formatCurrency, getAge, getClientAge, getClientName, getSpouseAge, getSpouseName } from '../../utils/formatters.js';

// Component imports
import { addFootersToAllPages, normalizeMargin, standardPDFStyles, addPageIfNeeded } from '../shared/PDFCommon.js';
import { addAssetLiabilityTables, addAbbreviationLegend, measureReportAmountTextWidth } from './PDFPrintMBS.js';
import { renderGeorgiaLegalCaption } from './georgia-legal-print-caption.js';

const getIncomeExpenseTableHeaders = (displayOptions) => {
    let configuration = "base";
    const cellWidths = {
        base: {
            index: 18,
            description: 400,
            notes: 90
        },
    }
    const defaultColumnStyle = {}
    const defaultCurrencyStyle = { halign: "right" };
    let headers = [
        { header: "#", dataKey: "index", columnStyle: { ...defaultColumnStyle, cellWidth: cellWidths[configuration].index } },
        { header: "Description", dataKey: "description", columnStyle: { ...defaultColumnStyle, cellWidth: cellWidths[configuration].description } },
        { header: "Amount", dataKey: "amount", columnStyle: { ...defaultColumnStyle, ...defaultCurrencyStyle } }
    ];
    return headers;
};

const calculateIncomeExpenseTableColumnStyles = (headers) => {
    let columnStyles = {};
    headers.forEach((header, index) => {
        columnStyles[index] = header.columnStyle || {};
    });
    return columnStyles;
};

const formatIncomeExpenseSectionTotalRow = (index, sectionName, sectionData, displayOptions) => {
    const sectionTotal = sectionData.reduce((sum, item) => sum + (parseFloat(item.stated_amount) || 0), 0);
    const sectionTotalRow = {
        rowType: "income_expense_total",
        index: index,
        description: `Total ${sectionName}`,
        amount: formatCurrency(sectionTotal),
    };
    index += 1;
    const result = [sectionTotalRow]
    return [result, index];
}

const formatIncomeExpenseTableData = (index, categoryName, IncomeExpenseTableData, displayOptions) => {
    const formatIncomeExpenseRow = (index, incomeExpenseRow) => {
        let result = [];
        if (incomeExpenseRow.stated_amount || 0 !== 0) {
            const accountRow = {
                rowType: "income_expense",
                index: index,
                description: incomeExpenseRow.subcategory || "",
                amount: formatCurrency(incomeExpenseRow.stated_amount),
            };
            if (displayOptions.showNotes) {
                accountRow.notes = incomeExpenseRow.notes || "";
            }
            index += 1;
            result.push(accountRow);
        }
        return [result, index];
    }

    const formatCategoryTotalRow = (index) => {
        const categoryTotal = IncomeExpenseTableData.reduce((sum, item) => sum + (parseFloat(item.stated_amount) || 0), 0);
        const categoryTotalRow = {
            rowType: "category_total",
            index: index,
            description: `${categoryName} Total`,
            amount: formatCurrency(categoryTotal),
        };
        index += 1;
        return [categoryTotalRow, index];
    }

    let result = []

    IncomeExpenseTableData.forEach((item) => {
        let rows;
        [rows, index] = formatIncomeExpenseRow(index, item);

        rows.forEach(row => {
            result.push(row);
        });
    });

    let [categoryTotalRow, newIndex] = formatCategoryTotalRow(index);

    result.push(categoryTotalRow);
    index = newIndex;

    return [result, index];
};

const addDRFAIncomeExpenseTable = (doc, margin, yPosition, categoryName, formattedIncomeExpenseData, displayOptions) => {
    const tableHeaders = getIncomeExpenseTableHeaders(displayOptions);
    const tableColumnStyles = calculateIncomeExpenseTableColumnStyles(tableHeaders);

    const cellFontSize = 5;
    const textHeaderHeight = 14;
    const textHeaderSpacing = 8;

    const formatRow = (data) => {
        const rowType = data.row.raw.rowType

        if (rowType === "category_total") {
            if (data.row.section === 'body') {
                data.cell.styles.fontStyle = standardPDFStyles.table.body.category_total.fontStyle;
                data.cell.styles.fillColor = standardPDFStyles.table.body.category_total.fillColor;
            }
        }

        if (data.row.section === 'body' && data.column.dataKey === 'index') {
            data.cell.styles.fontStyle = standardPDFStyles.table.body.index_column.fontStyle;
        }
    };

    const addCategoryName = (yPosition) => {
        doc.setFontSize(textHeaderHeight);
        doc.setFont(undefined, 'bold');

        margin = normalizeMargin(margin);

        doc.text(categoryName, margin.left, yPosition, { align: 'left' });
        yPosition += textHeaderSpacing;

        return yPosition;
    }

    function shouldStartNewPage(doc, currentY, margin, options = {}) {
        const {
            textHeaderHeight = 15,
            textHeaderSpacing = 10,
            tableRowCount = 10,
            rowHeight = 15,
            tableHeaderHeight = 20,
            footerMargin = 30,
            minRowsOnPage = 3
        } = options;

        margin = normalizeMargin(margin);

        const pageHeight = doc.internal.pageSize.height;
        const availableSpace = pageHeight - currentY - footerMargin - margin.bottom;

        const estimatedTableHeight = textHeaderSpacing + tableHeaderHeight + (tableRowCount * rowHeight);

        if (estimatedTableHeight > availableSpace) {
            return true;
        }

        const minTableHeight = textHeaderHeight + textHeaderSpacing + tableHeaderHeight + (minRowsOnPage * rowHeight);
        if (minTableHeight > availableSpace) {
            return true;
        }

        return false;
    }

    const shouldStartNewPageOptions = {
        textHeaderHeight: textHeaderHeight,
        textHeaderSpacing: textHeaderSpacing,
        tableRowCount: formattedIncomeExpenseData.length,
        rowHeight: 15,
        tableHeaderHeight: 20,
        footerMargin: 30,
        minRowsOnPage: 3
    }

    if (shouldStartNewPage(doc, yPosition, margin, shouldStartNewPageOptions)) {
        doc.addPage();
        yPosition = normalizeMargin(margin).top;
    }

    yPosition = addCategoryName(yPosition);

    autoTable(doc, {
        columns: tableHeaders,
        body: formattedIncomeExpenseData,
        startY: yPosition,
        pageBreak: 'avoid',
        styles: {
            overflow: 'linebreak',
            cellWidth: 'auto',
            font: 'times',
            fontSize: cellFontSize,
            valign: 'middle',
        },
        headStyles: {
            fillColor: standardPDFStyles.table.header.default.fillColor,
            textColor: standardPDFStyles.table.header.default.textColor,
        },
        columnStyles: tableColumnStyles,
        margin: { top: normalizeMargin(margin).top, left: normalizeMargin(margin).left, right: normalizeMargin(margin).right, bottom: normalizeMargin(margin).bottom },
        tableLineColor: [231, 76, 60],
        didParseCell: function (data) {
            formatRow(data);
        }
    });

    yPosition = doc.lastAutoTable.finalY + 8;

    return yPosition;
};

const addPersonalInformationSection = (doc, yPosition, margin, contentWidth, caseDetails, reportForClient) => {
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');

    const rowHeight = 18;
    const cellPadding = 4;

    const personalInfoRows = [
        {
            label: 'Your Name:',
            value: reportForClient ? getClientName(caseDetails) : getSpouseName(caseDetails),
            label2: 'Your Age:',
            value2: reportForClient ? getClientAge(caseDetails) : getSpouseAge(caseDetails)
        },
        {
            label: 'Spouse\'s Name:',
            value: reportForClient ? getSpouseName(caseDetails) : getClientName(caseDetails),
            label2: 'Spouse\'s Age:',
            value2: reportForClient ? getSpouseAge(caseDetails) : getClientAge(caseDetails)
        },
        {
            label: 'Date of Marriage:',
            value: caseDetails?.marriageDate ? formatDate(caseDetails.marriageDate) : 'UNKNOWN',
            label2: 'Date of Separation:',
            value2: caseDetails?.separationDate ? formatDate(caseDetails.separationDate) : 'UNKNOWN'
        }
    ];

    personalInfoRows.forEach(row => {
        doc.setFillColor(...standardPDFStyles.table.header.default.fillColor);
        doc.rect(margin, yPosition, contentWidth / 4, rowHeight, 'F');
        doc.rect(margin + contentWidth / 4, yPosition, contentWidth / 4, rowHeight);
        doc.rect(margin + contentWidth / 2, yPosition, contentWidth / 4, rowHeight, 'F');
        doc.rect(margin + (3 * contentWidth) / 4, yPosition, contentWidth / 4, rowHeight);

        doc.setDrawColor(...standardPDFStyles.table.body.default.lineColor);
        doc.setLineWidth(standardPDFStyles.table.body.default.lineWidth);
        doc.rect(margin, yPosition, contentWidth, rowHeight);
        doc.line(margin + contentWidth / 4, yPosition, margin + contentWidth / 4, yPosition + rowHeight);
        doc.line(margin + contentWidth / 2, yPosition, margin + contentWidth / 2, yPosition + rowHeight);
        doc.line(margin + (3 * contentWidth) / 4, yPosition, margin + (3 * contentWidth) / 4, yPosition + rowHeight);

        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.text(row.label, margin + cellPadding, yPosition + 12);
        doc.text(row.label2, margin + contentWidth / 2 + cellPadding, yPosition + 12);

        doc.setFont(undefined, 'normal');
        doc.text(row.value.toString(), margin + contentWidth / 4 + cellPadding, yPosition + 12);
        doc.text(row.value2?.toString(), margin + (3 * contentWidth) / 4 + cellPadding, yPosition + 12);

        yPosition += rowHeight;
    });

    const children = caseDetails?.children || [];
    const childrenNeedingSupport = children.filter(child => child.supportNeeded);
    const otherChildren = children.filter(child => !child.supportNeeded);

    yPosition += rowHeight;
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.text('Names and birth dates of children for whom support is to be determined in this action:', margin, yPosition);
    yPosition += rowHeight / 2;

    doc.setFillColor(...standardPDFStyles.table.header.default.fillColor);
    doc.rect(margin, yPosition, contentWidth, rowHeight, 'F');
    doc.setDrawColor(...standardPDFStyles.table.body.default.lineColor);
    doc.rect(margin, yPosition, contentWidth, rowHeight);
    doc.line(margin + contentWidth / 3, yPosition, margin + contentWidth / 3, yPosition + rowHeight);
    doc.line(margin + (2 * contentWidth) / 3, yPosition, margin + (2 * contentWidth) / 3, yPosition + rowHeight);

    doc.text('Name', margin + contentWidth / 6, yPosition + 12, { align: 'center' });
    doc.text('Date of Birth', margin + contentWidth / 2, yPosition + 12, { align: 'center' });
    doc.text('Resides with', margin + (5 * contentWidth) / 6, yPosition + 12, { align: 'center' });
    yPosition += rowHeight;

    if (childrenNeedingSupport.length > 0) {
        childrenNeedingSupport.forEach(child => {
            doc.rect(margin, yPosition, contentWidth, rowHeight);
            doc.line(margin + contentWidth / 3, yPosition, margin + contentWidth / 3, yPosition + rowHeight);
            doc.line(margin + (2 * contentWidth) / 3, yPosition, margin + (2 * contentWidth) / 3, yPosition + rowHeight);

            doc.setFont(undefined, 'normal');
            doc.text(child.fullName || 'NOT PROVIDED', margin + cellPadding, yPosition + 12);
            doc.text(child.birthDate ? formatDate(child.birthDate) : 'UNKNOWN', margin + contentWidth / 3 + cellPadding, yPosition + 12);
            doc.text(child.residesWith || 'NOT SPECIFIED', margin + (2 * contentWidth) / 3 + cellPadding, yPosition + 12);
            yPosition += rowHeight;
        });
    } else {
        doc.rect(margin, yPosition, contentWidth, rowHeight);
        doc.line(margin + contentWidth / 3, yPosition, margin + contentWidth / 3, yPosition + rowHeight);
        doc.line(margin + (2 * contentWidth) / 3, yPosition, margin + (2 * contentWidth) / 3, yPosition + rowHeight);

        doc.setFont(undefined, 'normal');
        doc.text('N/A', margin + cellPadding, yPosition + 12);
        doc.text('', margin + contentWidth / 3 + cellPadding, yPosition + 12);
        doc.text('', margin + (2 * contentWidth) / 3 + cellPadding, yPosition + 12);
        yPosition += rowHeight;
    }

    yPosition += rowHeight
    doc.setFont(undefined, 'bold');
    doc.text('Names and birth dates of your other children:', margin, yPosition);
    yPosition += rowHeight / 2;

    doc.setFillColor(...standardPDFStyles.table.header.default.fillColor);
    doc.rect(margin, yPosition, contentWidth, rowHeight, 'F');
    doc.setDrawColor(...standardPDFStyles.table.body.default.lineColor);
    doc.rect(margin, yPosition, contentWidth, rowHeight);
    doc.line(margin + contentWidth / 3, yPosition, margin + contentWidth / 3, yPosition + rowHeight);
    doc.line(margin + (2 * contentWidth) / 3, yPosition, margin + (2 * contentWidth) / 3, yPosition + rowHeight);

    doc.text('Name', margin + contentWidth / 6, yPosition + 12, { align: 'center' });
    doc.text('Date of Birth', margin + contentWidth / 2, yPosition + 12, { align: 'center' });
    doc.text('Resides with', margin + (5 * contentWidth) / 6, yPosition + 12, { align: 'center' });
    yPosition += rowHeight;

    if (otherChildren.length > 0) {
        otherChildren.forEach(child => {
            doc.rect(margin, yPosition, contentWidth, rowHeight);
            doc.line(margin + contentWidth / 3, yPosition, margin + contentWidth / 3, yPosition + rowHeight);
            doc.line(margin + (2 * contentWidth) / 3, yPosition, margin + (2 * contentWidth) / 3, yPosition + rowHeight);

            doc.setFont(undefined, 'normal');
            doc.text(child.fullName, margin + cellPadding, yPosition + 12);
            doc.text(child.birthDate ? formatDate(child.birthDate) : 'UNKNOWN', margin + contentWidth / 3 + cellPadding, yPosition + 12);
            doc.text(child.residesWith || 'NOT SPECIFIED', margin + (2 * contentWidth) / 3 + cellPadding, yPosition + 12);
            yPosition += rowHeight;
        });
    } else {
        doc.rect(margin, yPosition, contentWidth, rowHeight);
        doc.line(margin + contentWidth / 3, yPosition, margin + contentWidth / 3, yPosition + rowHeight);
        doc.line(margin + (2 * contentWidth) / 3, yPosition, margin + (2 * contentWidth) / 3, yPosition + rowHeight);

        doc.setFont(undefined, 'normal');
        doc.text('N/A', margin + cellPadding, yPosition + 12);
        doc.text('', margin + contentWidth / 3 + cellPadding, yPosition + 12);
        doc.text('', margin + (2 * contentWidth) / 3 + cellPadding, yPosition + 12);
        yPosition += rowHeight;
    }

    yPosition += 10; // Reduced spacing before summary section

    return yPosition
};

// MAXIMUM COMPACT: Summary section - matches document color theme
const addSummaryOfIncomeNeeds = (doc, yPosition, margin, contentWidth, pageWidth, drfaIncomeExpenseData, spousalSupport, childSupport, displayOptions = {}) => {
    // Calculate totals from data
    const currentGrossIncome = drfaIncomeExpenseData
        .filter(item => item.type === 'Income')
        .reduce((sum, item) => sum + (parseFloat(item.stated_amount) || 0), 0);

    const currentTotalExpenses = drfaIncomeExpenseData
        .filter(item => item.type !== 'Income')
        .reduce((sum, item) => sum + Math.abs(parseFloat(item.stated_amount) || 0), 0);

    const monthlyBalance = currentGrossIncome - currentTotalExpenses;

    const normalizedMargin = normalizeMargin(margin);

    // Header matching document theme
    const headerHeight = 18;
    doc.setFillColor(...standardPDFStyles.table.header.default.fillColor); // Match other tables
    doc.rect(normalizedMargin.left, yPosition, contentWidth, headerHeight, 'F');
    doc.setTextColor(...standardPDFStyles.table.header.default.textColor); // Match other tables
    doc.setFontSize(9.5);
    doc.setFont(undefined, 'bold');
    doc.text('Summary of Income & Needs', pageWidth / 2, yPosition + 12, { align: 'center' });
    yPosition += headerHeight;

    // Reset colors
    doc.setTextColor(0, 0, 0);

    // Create table data
    const summaryData = [
        { label: 'Gross monthly income (seen as "Total Income" below)', value: formatCurrency(currentGrossIncome) },
        { label: 'Average monthly expenses (seen as "Total Expenses" below)', value: formatCurrency(currentTotalExpenses) },
        { label: 'Net monthly income (Gross income minus expenses)', value: formatCurrency(monthlyBalance) }
    ];

    if (displayOptions.showSupportFields) {
        summaryData.push(
            { label: 'Amount of Spousal Support Needed', value: formatCurrency(spousalSupport || 0) },
            { label: 'Amount of Child Support Indicated by Child Support Guidelines', value: formatCurrency(childSupport || 0) }
        );
    }

    // Use autoTable with matching theme
    autoTable(doc, {
        startY: yPosition,
        head: [],
        body: summaryData.map(row => [row.label, row.value]),
        theme: 'grid',
        styles: {
            font: 'times',
            fontSize: 7.5,
            cellPadding: 3.5,
            lineColor: standardPDFStyles.table.body.default.lineColor, // Match other tables
            lineWidth: standardPDFStyles.table.body.default.lineWidth, // Match other tables
            minCellHeight: 11,
            textColor: [0, 0, 0] // Black text to match other tables
        },
        columnStyles: {
            0: { cellWidth: contentWidth * 0.75, halign: 'left' },
            1: { cellWidth: contentWidth * 0.25, halign: 'right', fontStyle: 'bold' }
        },
        margin: { left: normalizedMargin.left, right: normalizedMargin.right },
        didParseCell: function (data) {
            const rowIndex = data.row.index;

            // Remove colored values - keep consistent black text
            // Only highlight the net income row with background
            if (rowIndex === 2) {
                data.cell.styles.fillColor = standardPDFStyles.table.body.category_total.fillColor; // Match category total style
                data.cell.styles.fontStyle = 'bold';
                data.cell.styles.fontSize = 8;
            }
        }
    });

    yPosition = doc.lastAutoTable.finalY + 8;

    return yPosition;
};

// REFINED: Signature page - reduced top spacing
const addSignaturePage = (doc, margin, contentWidth, pageWidth, caseDetails, reportForClient) => {
    doc.addPage();
    let signatureY = normalizeMargin(margin).top + 30; // Reduced from 100 to 30
    const normalizedMargin = normalizeMargin(margin);

    // Perjury warning statement
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    const perjuryStatement = "I AM AWARE THAT ANY MATERIALLY FALSE STATEMENT KNOWINGLY MADE WITH THE INTENT TO DEFRAUD OR MISLEAD SHALL SUBJECT ME TO THE PENALTY FOR PERJURY AND MAY BE CONSIDERED A FRAUD UPON THE COURT.";
    const perjuryLines = doc.splitTextToSize(perjuryStatement, contentWidth);
    doc.text(perjuryLines, pageWidth / 2, signatureY, { align: 'center' });

    signatureY += (perjuryLines.length * 15) + 40;

    // Date line
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text('Dated: _________________', normalizedMargin.left, signatureY);

    signatureY += 40;

    // Signature line
    doc.line(pageWidth / 2 - 150, signatureY, pageWidth / 2 + 150, signatureY);
    signatureY += 15;
    doc.setFontSize(9);
    doc.text('Signature of party signing certificate and affidavit', pageWidth / 2, signatureY, { align: 'center' });

    signatureY += 10;

    // Printed name (pre-populated)
    const signerName = reportForClient ? getClientName(caseDetails) : getSpouseName(caseDetails);
    doc.text(`Printed Name: ${signerName}`, pageWidth / 2, signatureY, { align: 'center' });

    // Court section (blank for court to fill out)
    signatureY += 80;
    doc.line(normalizedMargin.left, signatureY - 10, pageWidth - normalizedMargin.right, signatureY - 10);
    signatureY += 10;

    doc.setFont(undefined, 'bold');
    doc.text('STATE OF GEORGIA', normalizedMargin.left, signatureY);
    signatureY += 20;
    doc.text(`COUNTY OF ${caseDetails?.county || '_______________'}`, normalizedMargin.left, signatureY);

    signatureY += 40;
    doc.setFont(undefined, 'normal');
    const swornText = 'Sworn to (or affirmed) and subscribed before me on the _____ day of ________, 20___.';
    doc.text(swornText, normalizedMargin.left, signatureY);

    signatureY += 60;

    // Notary signature line
    doc.line(normalizedMargin.left, signatureY, normalizedMargin.left + 200, signatureY);
    signatureY += 15;
    doc.setFontSize(8);
    doc.text('NOTARY PUBLIC-STATE OF GEORGIA', normalizedMargin.left, signatureY);
    signatureY += 12;
    doc.text('(Print, type or stamp commissioned name of notary)', normalizedMargin.left, signatureY);
};

const addIncomeExpenseTables = (doc, margin, rowIndex, yPosition, drfaIncomeExpenseData, whichSection, displayOptions) => {
    const addSectionTitle = (yPosition, sectionTitle) => {
        const textHeaderHeight = 16;
        const textHeaderSpacing = 10;

        doc.setFontSize(textHeaderHeight);
        doc.setFont(undefined, 'bold');

        doc.text(sectionTitle, normalizeMargin(margin).left, yPosition, { align: 'left' });
        yPosition += textHeaderSpacing;

        return yPosition;
    }

    const tableYMargin = 20;

    if (whichSection !== "income" && whichSection !== "expense") {
        console.error(`Invalid section: ${whichSection}`);
        return yPosition;
    }

    const sectionTitle = whichSection === "income" ? "Income" : "Expenses";

    yPosition = addSectionTitle(yPosition, sectionTitle);

    let sectionData;
    if (whichSection === "income") {
        sectionData = drfaIncomeExpenseData.filter(item => item.type === 'Income');
    } else {
        sectionData = drfaIncomeExpenseData.filter(item => item.type !== 'Income');
    }

    const groupedSectionData = sectionData.reduce((acc, item) => {
        if (!acc[item.category]) {
            acc[item.category] = [];
        }
        acc[item.category].push(item);
        return acc;
    }, {});

    Object.entries(groupedSectionData).forEach(([categoryName, items]) => {
        const hasItemsWithAmount = items.some(item => item.stated_amount && parseFloat(item.stated_amount) !== 0);
        if (hasItemsWithAmount) {
            let formattedIncomeExpenseData;
            [formattedIncomeExpenseData, rowIndex] = formatIncomeExpenseTableData(rowIndex, categoryName, items, displayOptions);
            yPosition += tableYMargin;
            yPosition = addDRFAIncomeExpenseTable(doc, margin, yPosition, categoryName, formattedIncomeExpenseData, displayOptions);
        }
    });

    yPosition += tableYMargin;

    let sectionTotalData;

    const sectionTotalName = `Total ${sectionTitle}`;
    [sectionTotalData, rowIndex] = formatIncomeExpenseSectionTotalRow(rowIndex, sectionTitle, sectionData, displayOptions);

    yPosition = addDRFAIncomeExpenseTable(doc, margin, yPosition, sectionTotalName, sectionTotalData, displayOptions);

    return [rowIndex, yPosition];
};

const usePrintDRFAPDF = (assetLiabilityData, drfaIncomeExpenseData, displayOptions, reportForClient, spousalSupport = 0, childSupport = 0) => {
    const { data: caseDetails } = useGetCaseDetails();
    const userProfileContext = useContext(UserProfileContext);
    const currentCase = userProfileContext?.userProfile?.currentCase || {};
    const currentOrg = userProfileContext?.userProfile?.currentOrg || {};

    const defaultPageOrientation = 'portrait';
    const defaultUnit = 'pt';
    const defaultPageFormat = 'letter';

    const addDRFATitle = (doc, pageWidth, margin, yPosition) => {
        yPosition = renderGeorgiaLegalCaption(
            doc,
            caseDetails,
            "DOMESTIC RELATIONS FINANCIAL AFFIDAVIT",
            {
                pageWidth: pageWidth,
                margin: normalizeMargin(margin).left,
                startY: yPosition
            }
        );

        return yPosition;
    };

    return () => {
        const doc = new jsPDF({
            orientation: defaultPageOrientation,
            unit: defaultUnit,
            format: defaultPageFormat
        });

        doc.setFont("Times", "normal");

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 45; // Further reduced from 50
        const contentWidth = pageWidth - (margin * 2);
        const drfaTableSpacing = 40;

        // Header - start with minimal top margin
        let yPosition = 35; // Further reduced from 40

        yPosition = addDRFATitle(doc, pageWidth, margin, yPosition);

        yPosition += 3; // Minimal spacing after title

        yPosition = addPersonalInformationSection(doc, yPosition, margin, contentWidth, caseDetails, reportForClient);
        yPosition = addPageIfNeeded(doc, margin, yPosition);

        // SUMMARY SECTION - Now compact enough to fit on page 1
        yPosition = addSummaryOfIncomeNeeds(doc, yPosition, margin, contentWidth, pageWidth, drfaIncomeExpenseData, spousalSupport, childSupport, displayOptions);

        // Start income section on new page
        doc.addPage();
        yPosition = normalizeMargin(margin).top;

        let rowIndex = 1;
        // Measured once so the Assets and Liabilities tables share one column layout.
        const amountTextWidth = measureReportAmountTextWidth(doc, assetLiabilityData, displayOptions);

        // INCOME SECTION
        [rowIndex, yPosition] = addIncomeExpenseTables(doc, margin, rowIndex, yPosition, drfaIncomeExpenseData, "income", displayOptions);

        yPosition += drfaTableSpacing;
        yPosition = addPageIfNeeded(doc, margin, yPosition);

        // ASSETS SECTION
        [rowIndex, yPosition] = addAssetLiabilityTables(doc, margin, rowIndex, yPosition, assetLiabilityData, "assets", displayOptions, { amountTextWidth });

        yPosition += drfaTableSpacing;
        yPosition = addPageIfNeeded(doc, margin, yPosition);

        // EXPENSES SECTION
        [rowIndex, yPosition] = addIncomeExpenseTables(doc, margin, rowIndex, yPosition, drfaIncomeExpenseData, "expense", displayOptions);

        yPosition += drfaTableSpacing;
        yPosition = addPageIfNeeded(doc, margin, yPosition);

        // LIABILITIES SECTION
        [rowIndex, yPosition] = addAssetLiabilityTables(doc, margin, rowIndex, yPosition, assetLiabilityData, "liabilities", displayOptions, { amountTextWidth });
        yPosition += drfaTableSpacing;

        // ABBREVIATION LEGEND — maps any short-form Title back to the full legal name
        yPosition = addPageIfNeeded(doc, margin, yPosition);
        yPosition = addAbbreviationLegend(doc, margin, yPosition, assetLiabilityData);

        // SIGNATURE PAGE - Added at the end
        addSignaturePage(doc, margin, contentWidth, pageWidth, caseDetails, reportForClient);

        // Add footers to all pages
        addFootersToAllPages(doc, currentOrg, displayOptions, margin)

        const filename = `DRFA_Sheet_${currentCase.caseName ? currentCase.caseName.replace(/[^a-zA-Z0-9]/g, '_') : 'Document'}_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(filename);
    }
}

export default usePrintDRFAPDF;